/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import Tooltip from "@mui/material/Tooltip"
import {
  DB_KEY_REDLINE_SYNC_STATE,
  selectCurrentStudyId,
  selectExperiment,
  selectExperimentId,
  selectGreenlineIdToProperties,
  selectGreenlineIdToRunState,
  selectSensorIdToName,
  selectStudy,
  setGreenlineIdToProperties,
  setSensorIdToBluelineRunState,
  setSensorIdToViolationsRunState,
  updateExperiment
} from "../../../../../../redux/ducks/SensingSystemArchitectureDesign"
import {useAppDispatch, useAppSelector} from "../../../../../../redux/hooks"
import toast from "react-hot-toast"
import {usePostRequest} from "../../../../../../api/helper"
import {SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS} from "../../../constants"
import {selectProgram} from "../../../../../../redux/ducks/AdasPrograms"
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {MRT_Cell, MRT_ColumnDef, MRT_GlobalFilterTextField, MRT_Row, MRT_RowSelectionState, MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleGlobalFilterButton} from "material-react-table";
import {cloneDeep} from "lodash";
import {ArgosAddIcon} from "../../../../../components/Icons/ArgosActionIcons";
import {ARGOS_ERROR, ARGOS_GREEN, ARGOS_WARNING, tableButtonGray} from "../../../../../../constants/colors";
import {Box, IconButton} from "@mui/material";
import {
  ConfigSensorWizardState,
  defaultDefinePropertiesStepData,
  defaultSelectSensorStepData,
  setConfigSensorWizardState,
  setSensorBomData,
  setSensorsFromDatabase
} from "../../../../../../redux/ducks/AdasPrograms/slice";
import {EAdasProgramsEditSensorDialogType} from "../../../../AdasPrograms/ItemDetails/SensorBom/types";
import HistoryIcon from '@mui/icons-material/History';
import {Container} from "../../../../CoverageRequirements/Table/constants";
import {getUrlForDataset, renderLink} from "../../../../CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties";
import {EditSensorDialog} from "./Dialogs/EditSensorDialog";
import GeneralWarningDialog from "./Dialogs/GeneralWarningDialog";
import {GreenLineDataSetStates} from "../../../types"
import {DownloadDataSetDialog} from "./Dialogs/DownloadDataSetDialog"
import {isGreenLineDataSetConfigured} from "../../helpers"
import {ViewDataSetErrorDialog} from "./Dialogs/ViewDataSetErrorDialog/ViewDataSetErrorDialog"
import {renderBlueLineStateCell, renderStateCell, renderViolationsStateCell} from "../helpers";
import SensorActionsCell from "./Table/SensorActionsCell";
import ResetSensorsToAdasBomDialog from "./Dialogs/ResetSensorsToAdasBomDialog";
import {ArgosTableSimple} from "../../../../../components/Table/ArgosTableSimple";

/**
 * Bill of Materials section of the Experiments panel on the Sensing System
 * Architecture Design View Study page
 */

export const BillOfMaterialsSection = () => {
  return (
    <ArgosFlexbox style={{padding: "20px", width: "100%"}}>
      <ArgosFlexbox vertical style={{flexGrow: 1, width: "100%"}}>
        <ArgosFlexbox style={{width: "100%"}}>
          <SensorBomTable filterAnalysis={false}/>
        </ArgosFlexbox>
      </ArgosFlexbox>
    </ArgosFlexbox>
  );
};

interface ISensorBomTableProps {
  filterAnalysis: boolean
}

/** Data structure of the Sensor Bill of Materials table rows */
export interface SensorBomTableData {
  /** The green line data set's ID */
  greenlineDatasetId: string

  /** The green line data set's state */
  state: GreenLineDataSetStates

  /** The blue line data set's state. */
  blueLineState: {
    state: GreenLineDataSetStates
    error?: {
      exception: string
      stackTrace: string
      timestamp: string
      activity: string
    }
  }

  /** The violation data set's state. */
  violationsState: {
    state: GreenLineDataSetStates
    error?: {
      exception: string
      stackTrace: string
      timestamp: string
      activity: string
    }
  }

  /** The sensor ID connected to the green line */
  sensorId: string

  /** The connected sensor's modality */
  modality: string

  /** The connected sensor's location key */
  sensorLocation: string

  /** The connected red line data set's ID or IDs */
  datasetId: string | string[]
}

export enum EResetSensorsToBomRequestType {
  RESET_BUT_KEEP_RED_LINE_DATA = 'resetButKeepRedLineData',
  RESET_ALL = 'resetAll'
}

export enum EReportStateChangeReason {
  RAW_DATASET_UPDATED = 'Raw Dataset Updated',
  DEVELOPMENT_STAGE_DATA_UPDATED = 'Development Stage Data Updated',
  ADAS_REPORT_DELETED = 'ADAS Report Deleted',
  EDITED_ADAS_REPORT = 'Edited ADAS Report',
  REGENERATED_MERGE = 'Regenerated Merge',
  EDITED_MERGE_SET = 'Edited Merge Set',
  MERGED_DATASET_DELETED = 'Merged Dataset Deleted',
  RAW_DATASET_DELETED = 'Raw Dataset Deleted',
}

export enum ERedlineSyncStateType {
  SYNCED = 'Synced',
  SYNCED_BUT_RAW_DATA_CHANGED = 'Synced but raw data changed',
  NOT_SYNCED_AND_RAW_DATA_CHANGED = 'Not synced and raw data changed',
}

export const COMBINED_SYNC_STATES = {
  ...EReportStateChangeReason,
  ...ERedlineSyncStateType,
} as const;
export type TCombinedSyncState = typeof COMBINED_SYNC_STATES[keyof typeof COMBINED_SYNC_STATES];

const SYNC_STATE_TO_COLOR: Partial<Record<TCombinedSyncState, string>> = {
  [COMBINED_SYNC_STATES.SYNCED]: ARGOS_GREEN,
  [COMBINED_SYNC_STATES.SYNCED_BUT_RAW_DATA_CHANGED]: ARGOS_WARNING,
  [COMBINED_SYNC_STATES.NOT_SYNCED_AND_RAW_DATA_CHANGED]: ARGOS_WARNING,
  [COMBINED_SYNC_STATES.RAW_DATASET_UPDATED]: ARGOS_WARNING,
  [COMBINED_SYNC_STATES.DEVELOPMENT_STAGE_DATA_UPDATED]: ARGOS_WARNING,
  [COMBINED_SYNC_STATES.ADAS_REPORT_DELETED]: ARGOS_ERROR,
  [COMBINED_SYNC_STATES.EDITED_ADAS_REPORT]: ARGOS_WARNING,
  [COMBINED_SYNC_STATES.REGENERATED_MERGE]: ARGOS_WARNING,
  [COMBINED_SYNC_STATES.EDITED_MERGE_SET]: ARGOS_WARNING,
  [COMBINED_SYNC_STATES.MERGED_DATASET_DELETED]: ARGOS_ERROR,
  [COMBINED_SYNC_STATES.RAW_DATASET_DELETED]: ARGOS_ERROR,
};

export const SensorBomTable = (props: ISensorBomTableProps) => {

  const dispatch = useAppDispatch();

  const currentStudyId = useAppSelector(selectCurrentStudyId);
  const study = useAppSelector((state) => selectStudy(state, currentStudyId ?? ""));
  const adasProgram = useAppSelector((state) => selectProgram(state, study?.adasProgramId ?? ""));
  const adasProgramId = adasProgram?.id;

  const experiment = useAppSelector(selectExperiment);
  const experimentId = useAppSelector(selectExperimentId)

  // setting this to reuse adas components
  useEffect(() => {
    if (!experiment) {
      return
    }
    dispatch(setSensorBomData({data: experiment.data}))
  }, [experiment]);

  const sensorIdToName = useAppSelector(selectSensorIdToName);

  const [sensorIdToInfo, setSensorIdToInfo] = useState<any>({})

  /**
   * For use in SensorStep dialog. TODO: Should we just move this to the SensorWizard component? For now, that component is same as in ADAS, so if we want to make is reusable in future, just keep this here for now.
   */
  const fetchSensorsFromDatabaseHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=fetchSensorsForDropdown')
  useEffect(() => {
    fetchSensorsFromDatabaseHandle().then((response: any) => {
      dispatch(setSensorsFromDatabase(response))
      // response is an object with modalities as keys and values as arrays of sensors, with sensor objects having sensorId, sensorName, sensorType, version. convert this to map of sensorId to all those properties.
      const _sensorIdToInfo: any = {}
      Object.values(response).forEach((sensors: any) => {
        sensors.forEach((sensor: any) => {
          _sensorIdToInfo[sensor.sensorId] = sensor
        })
      })
      setSensorIdToInfo(_sensorIdToInfo)
    }).catch((error: any) => {
      console.log(error)
    })
  }, [])

  const [resetSensorsWarningDialogData, setResetSensorsWarningDialogData] = useState<any>(null);
  const [deleteSensorWarningDialogData, setDeleteSensorWarningDialogData] = useState<any>(null);

  const importSensorsUrl = SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.importSensors;
  const importSensorsHandle = usePostRequest(importSensorsUrl);
  /**
   * - Handles the import of sensors from the ADAS BOM when resetting sensors.
   * - The dialog containing the buttons that call this function is opened when clicking the reset button in the Sensor BOM table.
   * @param requestType {EResetSensorsToBomRequestType} - The type of reset, either 'resetButKeepRedLineData' or 'resetAll'.
   */
  const handleImportSensors = (requestType: EResetSensorsToBomRequestType) => {
    const body = {adasProgramId, experimentId, studyId: currentStudyId, requestType};
    toast.loading("Importing sensors...", {id: "import-sensors-toast"});
    importSensorsHandle(body).catch((err) => {
      console.log(err);
      toast.error("An error occurred while importing sensors");
    }).finally(() => {
      toast.dismiss("import-sensors-toast");
    });
  };

  const sensorIdToBlueLineRunState = useAppSelector(state => state.sensingSystemArchitectureDesign.sensorIdToBlueLineRunState)
  const sensorIdToViolationsRunState = useAppSelector(state => state.sensingSystemArchitectureDesign.sensorIdToViolationsRunState)

  /**
   * Gets the initial state of the blue line run for each sensor based on the experiment data.
   * This is used to populate the blueLineState field in the SensorBomTableData.
   * @param _experiment - The current experiment data.
   * @returns {Record<string, any>} - A mapping of greenlineDatasetId to blueLineState.
   */
  const getInitialSensorIdToBlueLineRunState = (_experiment: any): Record<string, any> => {
    if (!_experiment?.data?.sensors) {
      return {};
    }
    const result: Record<string, any> = {};
    Object.values(_experiment.data.sensors).forEach((sensor: any) => {
      sensor.sensorLocations.forEach((location: any) => {
        if (location.greenlineDatasetId && location.blueLineState) {
          result[location.greenlineDatasetId] = location.blueLineState;
        }
      });
    });
    return result;
  }
  useEffect(() => {
    if (!experiment) {
      return;
    }
    // Initialize the sensorIdToBlueLineRunState with the initial state from the experiment data
    const initialSensorIdToBluelineRunState = getInitialSensorIdToBlueLineRunState(experiment);
    const data = {
      'type': 'upsert',
      'data': {
        'sensorIdToBlueLineRunState': initialSensorIdToBluelineRunState,
        experimentId
      },
    }
    dispatch(setSensorIdToBluelineRunState(data))
  }, [experiment, experimentId]);

  /**
   * Gets the initial state of the violation run for each sensor based on the experiment data.
   * This is used to populate the violationsState field in the SensorBomTableData.
   * @param _experiment - The current experiment data.
   * @returns {Record<string, any>} - A mapping of greenlineDatasetId to violationsState.
   */
  const getInitialSensorIdToViolationsRunState = (_experiment: any): Record<string, any> => {
    if (!_experiment?.data?.sensors) {
      return {};
    }
    const result: Record<string, any> = {};
    Object.values(_experiment.data.sensors).forEach((sensor: any) => {
      sensor.sensorLocations.forEach((location: any) => {
        if (location.greenlineDatasetId && location.violationsState) {
          result[location.greenlineDatasetId] = location.violationsState;
        }
      });
    });
    return result;
  }
  useEffect(() => {
    if (!experiment) {
      return;
    }
    // Initialize the sensorIdToViolationsRunState with the initial state from the experiment data
    const initialSensorIdToViolationsRunState = getInitialSensorIdToViolationsRunState(experiment);
    const data = {
      'type': 'upsert',
      'data': {
        'sensorIdToViolationsRunState': initialSensorIdToViolationsRunState,
        experimentId
      },
    }
    dispatch(setSensorIdToViolationsRunState(data))
  }, [experiment, experimentId]);


  const tableData: SensorBomTableData[] = useMemo(() => {
    /**
     * Generates the table data for the Sensor BOM table based on the experiment data.
     * @returns {SensorBomTableData[]}
     */
    if (!experiment?.data?.sensors) return [];
    const sensorConfig = experiment.data.sensors;
    return Object.values(sensorConfig).flatMap((sensor: any) => {
        return sensor.sensorLocations.map((location: any) => {
          return ({
            sensorLocation: location.locationKey,
            modality: sensor.sensorType,
            sensorId: sensor.sensorId,
            datasetId: location.redlineDatasetId,
            greenlineDatasetId: location.greenlineDatasetId,
            state: location.state,
            blueLineState: sensorIdToBlueLineRunState[location.greenlineDatasetId] ?? location.blueLineState,
            violationsState: sensorIdToViolationsRunState[location.greenlineDatasetId] ?? location.violationsState,
          })
        })
      }
    );
  }, [experiment, sensorIdToBlueLineRunState, sensorIdToViolationsRunState]);
  const filteredTableData: SensorBomTableData[] = useMemo(() => {
    /**
     * Filters the table data based on the sensorTableOrder from the experiment data. This ensures that the sensors are displayed in the order they were configured in the experiment.
     * If props.filterAnalysis is true, it filters out the rows that aren't selected for analysis based on the selectedSensors from the experiment data.
     * @returns {SensorBomTableData[]}
     */

    let retTableData = cloneDeep(tableData)

    const sensorTableOrder = experiment?.data?.sensorTableOrder
    // sensorTableOrder is array of sensorId_sensorLocation, so we need to sort the table data based on this order.
    if (sensorTableOrder) {
      retTableData.sort((a: any, b: any) => {
        return sensorTableOrder.indexOf(a.greenlineDatasetId) - sensorTableOrder.indexOf(b.greenlineDatasetId)
      })
    }

    // Filter out the rows that don't have analysis if props.filterAnalysis is true
    if (!props.filterAnalysis) {
      return retTableData
    }
    const selectedSensors = experiment?.data?.selectedSensors ?? {}
    retTableData = retTableData.filter((row: any) => selectedSensors[row.greenlineDatasetId])
    return retTableData
  }, [tableData, experiment, props.filterAnalysis])

  const [configureSensorDialogData, setConfigureSensorDialogData] = useState<any>(null)

  const editSensor = (id: string, modality: string, locationKey: string[], name: string, version: string, notSourced: boolean, greenlineDatasetId: string) => {
    const definePropertiesStepData = {
      ...defaultDefinePropertiesStepData,

    }
    const wizardState: ConfigSensorWizardState = {
      // action: EAdasProgramsEditSensorDialogType.EDIT_FROM_TABLE,
      action: EAdasProgramsEditSensorDialogType.EDIT_FROM_LOCATION,
      selectSensorStep: {
        ...defaultSelectSensorStepData,
        locationKey, oldLocationKey: locationKey, oldId: id,
        modality, name, version, id, notSourced,
      },
      definePropertiesStep: definePropertiesStepData,
      greenlineDatasetId
    }
    dispatch(setConfigSensorWizardState(wizardState))
  }

  /**
   * Opens the configure sensor dialog
   * @param cell
   * @returns {void}
   */
  const onClickConfigure = (cell: MRT_Cell<SensorBomTableData>) => {
    const sensorId = cell.row.original.sensorId;
    const sensorLocation = cell.row.original.sensorLocation;
    const sensor = experiment?.data.sensors[sensorId];
    if (!sensor) {
      console.error(`Sensor with id ${sensorId} not found`);
      return;
    }
    const location = sensor.sensorLocations.find((loc: any) => loc.locationKey === sensorLocation);
    if (!location) {
      console.error(`Location ${sensorLocation} not found for sensor ${sensorId}`);
      return;
    }
    const {sensorType: modality} = sensor;
    const locationKey = location.locationKey;

    const name = sensorIdToInfo[sensorId]?.sensorName
    const version = sensorIdToInfo[sensorId]?.version
    const notSourced = sensorId === "" || sensorId.endsWith("-not-sourced")

    const greenlineDatasetId = cell.row.original.greenlineDatasetId

    editSensor(sensorId, modality, locationKey, name, version, notSourced, greenlineDatasetId)
  }

  const removeSensorFromLocationHandle = usePostRequest('/HttpTriggerAdasPrograms?page=sensorBom&requestType=removeSensorFromLocation')
  const removeSensorFromLocation = useCallback((sensorId: string, locationKey: string[], adasProgramId: string, greenlineDatasetId: string) => {
    // TODO: there is a duplicate for this code in another file
    toast.loading('Removing sensor...', {id: "remove-sensor-toast"});
    removeSensorFromLocationHandle({adasProgramId, locationKey, sensorId, experimentId, studyId: currentStudyId, app: 'greenLine', greenlineDatasetId})
      .then((res: any) => {
        dispatch(setSensorBomData(res))
        dispatch(updateExperiment(res))
      }).catch((err: any) => {
      console.log(err)
      toast.error('Error removing sensor from location')
    }).finally(() => {
      toast.dismiss('remove-sensor-toast')
    })
  }, [experimentId, currentStudyId])
  const onClickDelete = (cell: MRT_Cell<SensorBomTableData>) => {
    const sensorId = cell.row.original.sensorId;
    const sensorLocation = cell.row.original.sensorLocation;
    if (!adasProgramId) {
      return
    }
    const greenlineDatasetId = cell.row.original.greenlineDatasetId
    removeSensorFromLocation(sensorId, [sensorLocation], adasProgramId, greenlineDatasetId)
  }

  const fetchHandleGetDatasetIdToData = usePostRequest(SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.getDatasetIdToData)
  const [datasetIdToData, setDatasetIdToData] = useState<any>({})
  useEffect(() => {
    if (!experimentId || !currentStudyId) {
      return
    }
    fetchHandleGetDatasetIdToData({studyId: currentStudyId, experimentId}).then((response: any) => {
      setDatasetIdToData(response)
    }).catch((error: any) => {
      console.log(error)
    })
  }, [currentStudyId, experimentId, experiment])

  const greenlineIdToRunState = useAppSelector(selectGreenlineIdToRunState)

  const greenlineIdToProperties = useAppSelector(selectGreenlineIdToProperties)

  const tableColumns: MRT_ColumnDef<SensorBomTableData>[] = [
    {
      id: "configure",
      header: "Actions",
      Cell: ({cell}) => {
        return (
          <SensorActionsCell
            cell={cell}
            setDeleteSensorWarningDialogData={setDeleteSensorWarningDialogData}
            onClickConfigure={onClickConfigure}
            greenlineIdToRunState={greenlineIdToRunState}
            tableButtonGray={tableButtonGray}
          />
        )
      }
    },
    {
      id: "state",
      header: "Green Line Data Set State",
      accessorFn: (row) => {
        const dataSetState = row.state
        return dataSetState ?? ''
      },
      Cell: ({cell, renderedCellValue}) => renderStateCell(cell, dispatch, renderedCellValue)(),
    },
    {
      id: "blueLineState",
      header: "Blue Line State",
      accessorFn: (row) => {
        const dataSetState = row.blueLineState?.state
        return dataSetState ?? ''
      },
      Cell: ({cell, renderedCellValue}) => renderBlueLineStateCell(cell, dispatch, renderedCellValue)(),
    },
    {
      id: "violationsState",
      header: "Violations State",
      accessorFn: (row) => {
        const dataSetState = row.violationsState?.state
        return dataSetState ?? ''
      },
      Cell: ({cell, renderedCellValue}) => renderViolationsStateCell(cell, dispatch, renderedCellValue)(),
    },
    {
      id: "redlineSyncState",
      header: "Green line synced with red line",
      accessorFn: (row) => {
        const greenlineDatasetId = row.greenlineDatasetId
        const redlineSyncState = greenlineIdToProperties[greenlineDatasetId]?.[DB_KEY_REDLINE_SYNC_STATE]
        return redlineSyncState
      },
      Cell: ({cell, renderedCellValue}) => {
        const row = cell.row.original
        const greenlineDatasetId = row.greenlineDatasetId

        const redlineSyncState = greenlineIdToProperties[greenlineDatasetId]?.[DB_KEY_REDLINE_SYNC_STATE] as TCombinedSyncState | undefined;
        const color = redlineSyncState ? (SYNC_STATE_TO_COLOR[redlineSyncState] ?? 'black') : 'black';

        return (
          <div style={{color}}>
            {renderedCellValue}
          </div>
        )
      },
    },
    {
      accessorKey: "sensorLocation",
      header: "Sensor Location",
    },
    {
      accessorKey: "modality",
      header: "Modality",
    },
    {
      id: "sensorName",
      header: "Sensor Name",
      accessorFn: (row) => {
        const sensorId = row.sensorId
        const notSourced = sensorId === "" || sensorId.endsWith("-not-sourced")
        let sensorName
        if (notSourced) {
          sensorName = sensorId
        } else {
          sensorName = sensorIdToName[row.sensorId] ?? sensorId
        }
        return sensorName ?? ''
      },
      Cell: ({renderedCellValue}) => {
        return (
          <div>{renderedCellValue}</div>
        )
      },
    },
    {
      'id': 'greenlineName',
      'header': 'Greenline Name',
      accessorFn: (row) => {
        return (greenlineIdToProperties?.[row.greenlineDatasetId]?.name ?? '')
      },
      Cell: ({renderedCellValue}) => {
        return (
          <div>{renderedCellValue}</div>
        )
      }
    },
    {
      // NOTE: The dataset names won't be highlighted from global search because the built-in way requires returning renderedCellValue which is the value from accessorFn which is not what we want to render here.
      id: "dataset",
      header: "Dataset",
      accessorFn: (row) => {
        let datasetIds = row.datasetId;
        if (!datasetIds) return '';
        if (typeof datasetIds === 'string') datasetIds = [datasetIds];
        return datasetIds
          .map((datasetId: string) => datasetIdToData[datasetId]?.name ?? '')
          .join(' ')
          .trim();
      },
      Cell: ({cell}) => {
        /**
         * Use the datasetId to get the dataset name and studyId and then create a link to the dataset.
         */
        let datasetIds = cell.row.original.datasetId
        if (!datasetIds) {
          return <div>None Selected</div>
        }
        if (typeof datasetIds === 'string') {
          datasetIds = [datasetIds]
        }

        if (datasetIds.length === 0) {
          return <div>None Selected</div>
        } else if (datasetIds.some((datasetId: string) => !datasetIdToData[datasetId])) {
          return <div style={{color: 'red'}}>Dataset Not Found</div>
        } else {
          return (
            <ArgosFlexbox>
              {datasetIds.map((datasetId: string) => {
                const datasetName = datasetIdToData[datasetId]?.name ?? ''
                const studyId = datasetIdToData[datasetId]?.studyId ?? ''
                const urlForDataset = getUrlForDataset(1, studyId, datasetId)
                return (
                  <div key={datasetId}>
                    {renderLink(datasetName, urlForDataset)}
                  </div>
                )
              })}
            </ArgosFlexbox>
          )
        }
      }
    }
  ]

  const handleAddSensor = () => {
    const wizardState: ConfigSensorWizardState = {
      action: EAdasProgramsEditSensorDialogType.ADD_GLOBAL,
      selectSensorStep: {...defaultSelectSensorStepData},
      definePropertiesStep: defaultDefinePropertiesStepData,
    }
    dispatch(setConfigSensorWizardState(wizardState))
  }

  const selectedSensors: MRT_RowSelectionState = useMemo<MRT_RowSelectionState>(() => {
    if (props.filterAnalysis) {
      return experiment?.data?.selectedSensorsForGreenlineModify ?? {}
    } else {
      return experiment?.data?.selectedSensors ?? {}
    }
  }, [experiment])

  // TODO: Since we are using selectedSensors as a state, when edit sensor or delete, need to update that state.
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(selectedSensors);
  useEffect(() => {
    // Using this state because otherwise typescript error.
    setRowSelection(selectedSensors);
  }, [selectedSensors]);

  /**
   * Handles the change in row selection
   * @param newRowSelection
   */
  const handleRowSelectionChange = (newRowSelection: any) => {
    // update the experiment with the new row selection
    const _experiment = experiment
    if (!_experiment) {
      return
    }

    // Since this table component is used for both table in bom section where you can add sensors and in section for editing green lines, we toggle which key to edit.
    const selectedSensorsKey = props.filterAnalysis ? 'selectedSensorsForGreenlineModify' : 'selectedSensors'

    const updatedExperiment = cloneDeep(_experiment)
    updatedExperiment.data[selectedSensorsKey] = newRowSelection

    // Since the sensors in the table associated with selectedSensorsForGreenlineModify are whatever selectedSensors are, when we change selectedSensors,
    // we need to update selectedSensorsForGreenlineModify if sensors that were removed from selectedSensors are in selectedSensorsForGreenlineModify, because then those sensors aren't even in the table.
    if (!props.filterAnalysis) {
      const selectedSensorsForGreenlineModify = updatedExperiment.data.selectedSensorsForGreenlineModify
      const selectedSensors = updatedExperiment.data.selectedSensors
      const keysToDelete = [];
      // Loop through selectedSensorsForGreenlineModify and if the sensor isn't in selectedSensors, add it to keysToDelete, which we will then use to delete the sensors from selectedSensorsForGreenlineModify.
      for (const sensorIdLocationKey in selectedSensorsForGreenlineModify) {
        if (!selectedSensors[sensorIdLocationKey]) {
          keysToDelete.push(sensorIdLocationKey);
        }
      }
      // Delete the keys that aren't in selectedSensors
      keysToDelete.forEach(key => {
        delete selectedSensorsForGreenlineModify[key];
      });
    }

    dispatch(updateExperiment(cloneDeep(updatedExperiment)));
  };

  const fetchHandleGetGreenlineIdToProperties = usePostRequest(SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.endpoints.getGreenlineIdToProperties)
  useEffect(() => {
    if (!experimentId || !currentStudyId) {
      return
    }
    fetchHandleGetGreenlineIdToProperties({studyId: currentStudyId, experimentId}).then((response: any) => {
      const _greenlineIdToProperties = response
      dispatch(setGreenlineIdToProperties({data: {greenlineIdToProperties: _greenlineIdToProperties, experimentId}, type: 'replace'}))

    }).catch((error: any) => {
      console.log(error)
    })
  }, [experiment, experimentId, currentStudyId]);

  const selectColTableHeaderTitle = props.filterAnalysis ? 'Generate' : 'Select for Analysis'


  const isRowSelectable = (row: MRT_Row<SensorBomTableData>) => {
    const sensorId = row.original.sensorId
    const locationKey = row.original.sensorLocation
    const greenLineConfigured = isGreenLineDataSetConfigured(sensorId, locationKey, experiment)
    const isGenerating = greenlineIdToRunState?.[row.original.greenlineDatasetId]?.inProgress ?? false
    return greenLineConfigured && !isGenerating
  }

  return (
    <Container style={{height: '700px', width: '100%'}}>
      <ArgosTableSimple
        key={JSON.stringify(greenlineIdToProperties)} // or use a version counter

        data={filteredTableData}
        columns={tableColumns}

        enableSelectAll={false}

        displayColumnDefOptions={{
          'mrt-row-select': {
            enableColumnActions: true,
            enableHiding: true,
            size: 200,
            header: selectColTableHeaderTitle
          },
        }}

        getRowId={(row) => row.greenlineDatasetId}
        enableRowSelection={(row) => isRowSelectable(row) && !study?.archive}
        onRowSelectionChange={(updater: any) => {
          const prev = selectedSensors;
          const newState = updater(prev);
          handleRowSelectionChange(newState);
        }}
        state={{rowSelection}}

        renderTopToolbar={({table}) => (
          <Box
            sx={{
              display: 'flex', justifyContent: 'flex-end', alignItems: 'center', backgroundColor: '#F5F6F7',
              width: '100%', paddingLeft: '5px'
            }}
          >
            <div>
              {!props.filterAnalysis && !study?.archive &&
                <>
                  <Tooltip title={`Reset sensors to ADAS BOM`} arrow>
                    <IconButton
                      onClick={() => {
                        setResetSensorsWarningDialogData(true)
                      }}
                      size='large'
                    >
                      <HistoryIcon style={{color: tableButtonGray}}/>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Add Sensor`} arrow>
                    <IconButton onClick={handleAddSensor} size='large'>
                      <ArgosAddIcon style={{color: tableButtonGray}}/>
                    </IconButton>
                  </Tooltip>
                </>
              }
            </div>
            <Box style={{justifySelf: 'flex-end'}}>
              {tableToolbarActions(table)}
            </Box>
          </Box>
        )}
      />

      <EditSensorDialog data={configureSensorDialogData} closeDialog={() => {
        setConfigureSensorDialogData(null)
      }}/>

      {/*Reset sensors dialog*/}
      <ResetSensorsToAdasBomDialog
        open={!!resetSensorsWarningDialogData}
        message={'Are you sure you want to reset sensors to the ADAS BOM? This will reset all data.'}
        onClose={() => {
          setResetSensorsWarningDialogData(false)
        }}
        resetButKeepRedLineData={() => {
          handleImportSensors(EResetSensorsToBomRequestType.RESET_BUT_KEEP_RED_LINE_DATA)
          setResetSensorsWarningDialogData(false)
        }}
        resetAll={() => {
          handleImportSensors(EResetSensorsToBomRequestType.RESET_ALL)
          setResetSensorsWarningDialogData(false)
        }}
      />

      {/*Delete sensor dialog*/}
      <GeneralWarningDialog
        open={!!deleteSensorWarningDialogData}
        message={'Are you sure you want to delete this sensor? This will delete all data that was configured in this app.'}
        onClose={() => {
          setDeleteSensorWarningDialogData(null)
        }}
        onConfirm={() => {
          onClickDelete(deleteSensorWarningDialogData)
          setDeleteSensorWarningDialogData(null)
        }}
      />

      {/** Download data set dialog */}
      <DownloadDataSetDialog/>

      {/** View data set errors dialog */}
      <ViewDataSetErrorDialog/>

    </Container>
  )
}

const tableToolbarActions = (table: any) => {
  return (
    <ArgosFlexbox>
      <MRT_GlobalFilterTextField table={table}/>
      <MRT_ToggleGlobalFilterButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ToggleFiltersButton table={table} style={{color: tableButtonGray}}/>
      <MRT_ShowHideColumnsButton table={table} style={{color: tableButtonGray}}/>
    </ArgosFlexbox>
  )
}


